import { createFocusTrap, type FocusTrap } from "focus-trap";
import { onBeforeUnmount, watch, type Ref } from "vue";
import { nextTick } from "vue";

type Options = {
  /** When false, Escape is left to the host (e.g. Modal.vue). Default false. */
  escapeDeactivates?: boolean;
  /**
   * Passed through to focus-trap. If omitted, outside clicks are blocked except on teleported
   * UI (Dropdown `.dropdown__panel`, body-teleported sheets/confirmations — see allowOutsideClick).
   */
  allowOutsideClick?: boolean | ((event: MouseEvent | TouchEvent) => boolean);
};

const TELEPORTED_DIALOG_SELECTORS = [
  ".dropdown__panel",
  ".goods-confirm-modal",
  ".goods-confirm-modal__backdrop",
  ".goods-confirm-modal__dialog",
  ".photo-section-confirm",
  ".photo-section-confirm__backdrop",
  ".photo-section-confirm__card",
  ".photo-section-add-modal",
  ".photo-section-add-modal__backdrop",
  ".photo-section-add-modal__card",
  ".wb-connect-modal",
  ".wb-connect-modal-overlay",
  ".pgt-sheet",
  ".pgt-sheet__panel",
  ".pfs-sheet",
  ".pfs-sheet__panel",
  ".pfs-overlay",
  ".pfs-backdrop",
  ".pfs-modal",
  ".gvfs-overlay",
  ".gvfs-backdrop",
  ".gvfs-modal",
  ".settings-confirm-modal",
  ".settings-confirm-modal__backdrop",
  ".settings-confirm-modal__dialog",
  ".insufficient-geni",
  ".insufficient-geni__backdrop",
  ".insufficient-geni__card",
  ".ai-header-confirm-modal",
  ".ai-header-confirm-modal__backdrop",
  ".ai-header-confirm-modal__dialog",
  ".itb-confirm",
  ".itb-confirm__backdrop",
  ".itb-confirm__card",
  ".assistent-modal-overlay",
  ".tent-modal-overlay",
  ".assistent-modal-backdrop",
  ".assistent-chat-modal",
  ".tent-chat-modal",
  ".chat-image-overlay",
  ".gallery-download-modal",
  ".gallery-download-modal__backdrop",
  ".gallery-download-modal__dialog",
  ".ui-image__overlay",
  ".ui-image__backdrop",
  ".ui-image__viewer",
  ".good-versions-images-sheet",
  ".good-versions-images-sheet__panel",
  ".modal-overlay",
  ".modal-overlay__backdrop",
  ".modal-card",
] as const;

function getEventTargetElement(target: EventTarget | null): Element | null {
  if (target instanceof Element) return target;
  if (target instanceof Node) return target.parentElement;
  return null;
}

/**
 * Activates focus-trap while `isOpen` is true on `containerRef` element.
 * Restores focus to the previously focused element on deactivate (WCAG 2.1.2, 2.4.3).
 */
export function useFocusTrapDialog(isOpen: Ref<boolean>, containerRef: Ref<HTMLElement | null>, options?: Options) {
  let trap: FocusTrap | null = null;

  const deactivate = () => {
    if (trap) {
      trap.deactivate();
      trap = null;
    }
  };

  watch(
    isOpen,
    async (open) => {
      if (!open) {
        deactivate();
        return;
      }
      await nextTick();
      await new Promise<void>((r) => requestAnimationFrame(() => r()));
      const el = containerRef.value;
      if (!el || !isOpen.value) return;

      const userAllowOutside = options?.allowOutsideClick;
      trap = createFocusTrap(el, {
        escapeDeactivates: options?.escapeDeactivates ?? false,
        allowOutsideClick(event) {
          // const target = event.target as HTMLElement | null;
          const target = getEventTargetElement(event.target);
          /* Программный <a download> в document.body (blob) — иначе focus-trap: preventDefault + stopImmediatePropagation на capture */
          if (target?.closest?.("[data-genitrap-bypass]")) return true;
          if (target?.closest?.(".dropdown__panel")) return true;
          /* Teleport в body (вне modal-card): иначе клики по подтверждению не доходят до кнопок */
          if (target?.closest?.(".goods-confirm-modal")) return true;
          if (target?.closest?.(".photo-section-confirm")) return true;
          if (target?.closest?.(".photo-section-add-modal")) return true;
          /* Закрытие по клику на затемнение — подложка вне карточки в Modal.vue */
          if (target?.closest?.(".modal-overlay__backdrop")) return true;
          /* Чат «Своя идея» (AIAssistentModal, Teleport body — вне modal-card) */
          if (target?.closest?.(".assistent-modal-overlay")) return true;
          if (target?.closest?.(".tent-modal-overlay")) return true;
          /* Полноэкранный просмотр фото из чата (отдельный Teleport, вне assistent-chat-modal) */
          if (target?.closest?.(".chat-image-overlay")) return true;
          /* Bottom sheet меню фото (Teleport body — вне modal-card) */
          if (target?.closest?.(".pgt-sheet")) return true;
          if (target?.closest?.(".pfs-sheet")) return true;
          if (target?.closest?.(".good-versions-images-sheet")) return true;
          if (target?.closest?.(".mps-sheet")) return true;
          /* Подтверждение перегенерации текста (ImproveTextBlock, Teleport body) */
          if (target?.closest?.(".itb-confirm")) return true;
          if (target?.closest?.(".insufficient-geni")) return true;

          /* Teleported panels/dialogs rendered outside the trapped modal card. */
          if (target && TELEPORTED_DIALOG_SELECTORS.some((selector) => target.closest(selector))) return true;
          if (userAllowOutside === true) return true;
          if (userAllowOutside === false) return false;
          if (typeof userAllowOutside === "function") return userAllowOutside(event);
          return false;
        },
        returnFocusOnDeactivate: true,
        fallbackFocus: el,
      });
      trap.activate();
    },
    { flush: "post" }
  );

  onBeforeUnmount(() => deactivate());
}
